import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, inject } from '@angular/core';
import { PluginPreviewSheetComponent } from '../plugin-preview-sheet/plugin-preview-sheet.component';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import { CompanyService, Company } from '../../services/company.service';
import { AuthService } from '../../services/auth.service';
import { StorefrontService } from '../../services/storefront.service';
import { FinancialSettingsComponent } from './financial-settings/financial-settings.component';
import { IntegrationSettingsComponent } from './integration-settings/integration-settings.component';
import { CompanySettingsComponent } from './company-settings/company-settings.component';
import { StorefrontSettingsComponent } from './storefront-settings/storefront-settings.component';
import { InvoicingComponent } from './invoicing/invoicing.component';
import { AccountingStatementComponent } from './accounting-statement/accounting-statement.component';
import { PayoutStatementsComponent } from './payout-statements/payout-statements.component';
import { QrCodeSettingsComponent } from './qr-code-settings/qr-code-settings.component';
import { SellSettingsComponent } from './sell-settings/sell-settings.component';
import { BrandSettingsComponent } from './brand-settings/brand-settings.component';
import { VoucherDesignSettingsComponent } from './voucher-design-settings/voucher-design-settings.component';
import { FeesSettingsComponent } from './fees-settings/fees-settings.component';
import { RedemptionPinSettingsComponent } from './redemption-pin-settings/redemption-pin-settings.component';
import { VerbundSettingsComponent } from './verbund-settings/verbund-settings.component';
import { NotificationSettingsComponent } from './notification-settings/notification-settings.component';
import { GroupManagementComponent } from '../../views/group-management/group-management.component';
import { SettingsSidebarComponent } from './settings-sidebar/settings-sidebar.component';
import { SETTINGS_NAV, findNavItem, isNavItemVisible, navLabel } from './settings-nav';
import { SettingsNavStateService } from './settings-nav-state.service';
import { BankAccountFormComponent } from '../bank-account-form/bank-account-form.component';
import { VerbundSettlementsComponent } from './verbund-settlements/verbund-settlements.component';
import { ReferralSettingsComponent } from './referral-settings/referral-settings.component';
import { PartnerAccessSettingsComponent } from './partner-access-settings/partner-access-settings.component';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [
    PluginPreviewSheetComponent,
    HlmTypographyImports,
    SettingsSidebarComponent,
    FinancialSettingsComponent,
    IntegrationSettingsComponent,
    CompanySettingsComponent,
    StorefrontSettingsComponent,
    InvoicingComponent,
    AccountingStatementComponent,
    PayoutStatementsComponent,
    QrCodeSettingsComponent,
    SellSettingsComponent,
    BrandSettingsComponent,
    VoucherDesignSettingsComponent,
    FeesSettingsComponent,
    RedemptionPinSettingsComponent,
    VerbundSettingsComponent,
    NotificationSettingsComponent,
    GroupManagementComponent,
    BankAccountFormComponent,
    VerbundSettlementsComponent,
    ReferralSettingsComponent,
    PartnerAccessSettingsComponent,
    HlmCardImports,
  ],
  templateUrl: './settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class SettingsComponent implements OnInit, OnDestroy {
  company: Company | null = null;
  private readonly navState = inject(SettingsNavStateService);

  private _activeParam: string | null = null;
  /** The `tab` query param; identifies the visible panel. Shared with the mobile menu. */
  get activeParam(): string | null {
    return this._activeParam;
  }
  set activeParam(param: string | null) {
    this._activeParam = param;
    this.navState.activeParam.set(param);
  }

  private destroy$ = new Subject<void>();

  constructor(
    private companyService: CompanyService,
    private router: Router,
    private route: ActivatedRoute,
    protected authService: AuthService,
    private storefrontService: StorefrontService,
  ) {}

  ngOnInit() {
    if (this.authService.isLoggedIn() && !this.storefrontService.isStorefront()) {
      this.companyService.company$.pipe(takeUntil(this.destroy$)).subscribe((company: Company) => {
        const isProfileCompleted = this.authService.isProfileCompleted();
        const hasValidStoreId = !!company?.public_id;
        const wasEmpty = !this.company;
        this.company = hasValidStoreId && isProfileCompleted ? company : null;

        // The role decides which entry comes first, so re-evaluate once it is known
        if (wasEmpty && this.company && !this.route.snapshot.queryParams['tab']) {
          this.activeParam = this.firstVisibleParam();
        }
      });
    }

    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params: Params) => {
      // Fees and PIN were two pages for a while; keep those links working.
      const tab =
        params['tab'] === 'fees' || params['tab'] === 'redemption-pin'
          ? 'fees-redemption'
          : params['tab'];
      this.activeParam = tab && findNavItem(tab) ? tab : this.firstVisibleParam();
    });

    this.route.fragment.pipe(takeUntil(this.destroy$)).subscribe((fragment) => {
      if (!fragment) return;
      // Wait for the panel to render before scrolling to the anchor
      setTimeout(() => this.highlightFragment(fragment), 350);
    });
  }

  onSelect(param: string) {
    this.activeParam = param;
    this.router.navigate([], {
      queryParams: { tab: param },
      queryParamsHandling: 'merge',
    });
  }

  isActiveParam(param: string): boolean {
    return this.activeParam === param;
  }

  get currentTitle(): string {
    const item = this.activeParam ? findNavItem(this.activeParam) : undefined;
    if (!item) return 'Einstellungen';
    return navLabel(item, this.navAudience);
  }

  get navAudience() {
    return {
      isMember: !!this.company?.is_member,
      isNetwork: !!this.company?.is_group_admin,
      isActingPartner: !!this.authService.actingPartnerName(),
    };
  }

  /** Members don't see every entry, so the landing tab depends on the role. */
  private firstVisibleParam(): string {
    const who = this.navAudience;
    for (const group of SETTINGS_NAV) {
      for (const item of group.items) {
        if (!isNavItemVisible(item, who) || item.notLanding) continue;
        if (item.children) {
          const child = item.children.find((sub) => isNavItemVisible(sub, who) && !sub.notLanding);
          if (child) return child.param;
          continue;
        }
        return item.param;
      }
    }
    return 'company';
  }

  private highlightFragment(fragment: string) {
    const el = document.getElementById(fragment);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('setting-highlight');
    setTimeout(() => {
      el.classList.remove('setting-highlight');
      el.classList.add('setting-highlight-fade');
      setTimeout(() => el.classList.remove('setting-highlight-fade'), 800);
    }, 2800);
  }

  ngOnDestroy() {
    this.navState.activeParam.set(null);
    this.destroy$.next();
    this.destroy$.complete();
  }
}
